import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api';
import { MicroLabel } from './micro-label';
import { Button } from './button';

/**
 * What a failed query looks like. Every screen previously fell back to `?? []` or `?? '—'`, or in
 * one case returned null, so "loading", "empty" and "the request failed" were indistinguishable —
 * a 500 rendered as an empty table or a blank page, and nothing told the user to retry.
 *
 * The trace id is shown when the API supplied one: it is the only handle support has on a failure
 * that has already scrolled out of a log.
 */
export function ErrorState({
  error,
  onRetry,
  title = 'Could not load this',
  className
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  className?: string;
}) {
  const api = error instanceof ApiError ? error : null;
  const detail = api?.message ?? (error instanceof Error ? error.message : 'Something went wrong.');

  return (
    <div
      className={cn(
        'grid place-items-center rounded border border-danger/40 bg-card px-6 py-16 text-center',
        className
      )}
      role="alert"
    >
      <div className="grid justify-items-center gap-3">
        <AlertTriangle className="text-danger" size={20} aria-hidden />
        <p className="text-[15px] text-foreground">{title}</p>
        <p className="max-w-prose text-[13px] text-muted">{detail}</p>
        {api?.traceId ? <MicroLabel size="xs">trace {api.traceId}</MicroLabel> : null}
        {onRetry ? (
          <Button variant="secondary" onClick={onRetry} className="mt-1">
            Try again
          </Button>
        ) : null}
      </div>
    </div>
  );
}
